import { z } from 'zod'

import { parsePhoneNumberFromString } from 'libphonenumber-js/max'

const INVALID_PHONE_NUMBER_MESSAGE = 'Zadajte platné mobilné telefónne číslo'
const WRONG_LENGTH_MESSAGE = 'Telefónne číslo musí mať 9 číslic (bez úvodnej 0)'

// Povolené znaky v telefónnom čísle: iba číslice a medzery.
// Predvoľba (+421 a pod.) sa vyberá samostatne, preto v čísle nie je povolená.
// Definujeme len to, čo je povolené — všetko ostatné je automaticky neplatné.
export const ALLOWED_PHONE_NUMBER = /^[\d\s]+$/

const NATIONAL_DIGITS_BY_PREFIX: Record<string, number> = {
  '+421': 9,
  '+420': 9,
}

function hasValidNationalLength({
  prefix,
  number,
}: {
  prefix: string
  number: string
}) {
  const expectedDigits = NATIONAL_DIGITS_BY_PREFIX[prefix]
  const digitCount = number.replace(/\D/g, '').length
  return expectedDigits === undefined || digitCount === expectedDigits
}

// Formulár posiela SMS kód → akceptujeme len mobilné čísla. FIXED_LINE_OR_MOBILE
// je pre krajiny, kde knižnica mobil a pevnú linku nevie rozlíšiť.
function isValidMobilePhoneNumber({
  prefix,
  number,
}: {
  prefix: string
  number: string
}) {
  const parsed = parsePhoneNumberFromString(`${prefix}${number}`)
  if (!parsed?.isValid()) return false
  const type = parsed.getType()
  return type === 'MOBILE' || type === 'FIXED_LINE_OR_MOBILE'
}

const phoneNumberFields = {
  prefix: z.string().min(1, 'Predvoľba je povinná'),
  number: z
    .string()
    .trim()
    .min(1, 'Telefónne číslo je povinné')
    .regex(
      ALLOWED_PHONE_NUMBER,
      'Telefónne číslo môže obsahovať iba číslice a medzery'
    ),
}

export const phoneNumberSchema = z
  .object(phoneNumberFields)
  .refine(isValidMobilePhoneNumber, {
    message: INVALID_PHONE_NUMBER_MESSAGE,
    path: ['number'],
  })
  .refine(hasValidNationalLength, {
    message: WRONG_LENGTH_MESSAGE,
    path: ['number'],
  })

export type PhoneNumberSchema = z.infer<typeof phoneNumberSchema>

export const phoneNumberWithGdprSchema = z
  .object({
    ...phoneNumberFields,
    gdpr: z.boolean().refine((value) => value === true, {
      message: 'Označte prosím, že súhlasíte so spracovaním osobných údajov',
    }),
    marketing: z.boolean(),
  })
  .refine(isValidMobilePhoneNumber, {
    message: INVALID_PHONE_NUMBER_MESSAGE,
    path: ['number'],
  })
  .refine(hasValidNationalLength, {
    message: WRONG_LENGTH_MESSAGE,
    path: ['number'],
  })

export type PhoneNumberWithGdprSchema = z.infer<
  typeof phoneNumberWithGdprSchema
>
